// LEITOR DE LARGURA FIXA — campo por posicao, sem separador.
//
// E' o `largura-fixa` da dimensao `delimitacao` da matriz. Existe ao lado do
// motor legado `fixed-width.ts` (raiz do pacote), e nao em cima dele, porque
// aquele conhece TIPOS (`centavos`, `data`, `codigo`) e o registro Atlas de
// 240 posicoes; aqui um campo e' so' um PEDACO DE TEXTO. O que o pedaco
// significa e' assunto de `numero.ts`, `data.ts` e do adaptador — e' o que
// deixa o mesmo leitor servir a qualquer ente, com qualquer semantica.
//
// `inicio` e' 1-based e INCLUSIVO, como em `fixed-width.ts` e como todo
// caderno de layout que o integrador tem na mao ("posicoes 1 a 8"). A
// conversao para indice de string acontece aqui e em nenhum outro lugar.
//
// Largura e' contada em caracteres. Arquivo Latin-1 tem um byte por
// caractere, entao a conta do caderno bate; a borda ja decodificou os bytes
// antes de o dominio ver a linha (ADR-0007).
/** Declaracao de campos inconsistente, ou valor que nao cabe na escrita. */
export class LarguraFixaInvalida extends Error {
    constructor(mensagem) {
        super(mensagem);
        this.name = "LarguraFixaInvalida";
    }
}
/**
 * A largura da linha inteira: o fim do ultimo campo. E' o que um adaptador
 * usa para reconhecer o arquivo pelo conteudo ("toda linha tem N posicoes").
 */
export function larguraTotal(campos) {
    return campos.reduce((max, c) => Math.max(max, c.inicio - 1 + c.largura), 0);
}
/**
 * Campos com posicao valida, sem sobreposicao e sem nome repetido. Lanca
 * porque e' erro de quem declarou o layout, nao do arquivo — um campo que
 * pisa em outro faria a escrita sobrescrever em silencio.
 */
function conferirCampos(campos) {
    const nomes = new Set();
    for (const c of campos) {
        if (!Number.isInteger(c.inicio) || c.inicio < 1) {
            throw new LarguraFixaInvalida(`campo "${c.nome}": inicio ${c.inicio} — a primeira posicao e' 1`);
        }
        if (!Number.isInteger(c.largura) || c.largura < 1) {
            throw new LarguraFixaInvalida(`campo "${c.nome}": largura ${c.largura} — precisa ser >= 1`);
        }
        if (c.preenchimento !== undefined && c.preenchimento.length !== 1) {
            throw new LarguraFixaInvalida(`campo "${c.nome}": preenchimento "${c.preenchimento}" precisa ter um caractere`);
        }
        if (nomes.has(c.nome))
            throw new LarguraFixaInvalida(`campo "${c.nome}" declarado duas vezes`);
        nomes.add(c.nome);
    }
    const ordenados = [...campos].sort((a, b) => a.inicio - b.inicio);
    for (let i = 1; i < ordenados.length; i++) {
        const antes = ordenados[i - 1];
        const este = ordenados[i];
        if (este.inicio < antes.inicio + antes.largura) {
            throw new LarguraFixaInvalida(`campo "${este.nome}" (posicao ${este.inicio}) sobrepoe "${antes.nome}" (${antes.inicio}-${antes.inicio + antes.largura - 1})`);
        }
    }
}
/**
 * Tira o preenchimento do lado em que ele fica: a direita do texto alinhado a
 * esquerda, a esquerda do numero alinhado a direita. So' o lado declarado —
 * um espaco a esquerda de um texto e' conteudo, nao preenchimento.
 *
 * Campo feito SO' de preenchimento: vazio quando o preenchimento e' branco,
 * UM preenchimento quando nao e'. "000000" e' zero, nao ausencia — devolver ""
 * faria `lerNumero` recusar todo valor zerado do arquivo.
 */
function despir(bruto, campo) {
    const pad = campo.preenchimento ?? " ";
    let s = bruto;
    if (campo.alinhamento === "direita") {
        let i = 0;
        while (i < s.length && s[i] === pad)
            i += 1;
        s = s.slice(i);
    }
    else {
        let fim = s.length;
        while (fim > 0 && s[fim - 1] === pad)
            fim -= 1;
        s = s.slice(0, fim);
    }
    if (s === "" && bruto !== "" && pad.trim() !== "")
        return pad;
    return s;
}
/**
 * Os campos de uma linha, pelo nome. Linha mais curta que o layout nao e'
 * erro aqui: o campo que cai fora da linha vem vazio (e o que cai pela metade
 * vem com o que houver). Quem sabe se isso e' recusa ou linha em branco de
 * fim de arquivo e' o adaptador, com a matriz na mao.
 */
export function lerLarguraFixa(linha, campos) {
    conferirCampos(campos);
    const valores = {};
    for (const c of campos) {
        const bruto = linha.slice(c.inicio - 1, c.inicio - 1 + c.largura);
        valores[c.nome] = despir(bruto, c);
    }
    return valores;
}
/**
 * A linha a partir dos valores. Campo sem valor sai todo em preenchimento;
 * lacuna entre campos (posicoes que nenhum campo cobre) sai em espaco. Valor
 * maior que a largura LANCA: cortar em silencio e' como um CPF vira outro.
 *
 * Escrever e ler de volta devolve os mesmos valores (para valor que nao
 * termina nem comeca pelo proprio preenchimento — ambiguidade inerente ao
 * formato, nao a este leitor).
 */
export function escreverLarguraFixa(valores, campos) {
    conferirCampos(campos);
    const saida = new Array(larguraTotal(campos)).fill(" ");
    for (const c of campos) {
        const valor = valores[c.nome] ?? "";
        if (valor.length > c.largura) {
            throw new LarguraFixaInvalida(`campo "${c.nome}": "${valor}" tem ${valor.length} caracteres e a largura e' ${c.largura}`);
        }
        const pad = c.preenchimento ?? " ";
        const cheio = c.alinhamento === "direita" ? valor.padStart(c.largura, pad) : valor.padEnd(c.largura, pad);
        for (let i = 0; i < c.largura; i++)
            saida[c.inicio - 1 + i] = cheio[i];
    }
    return saida.join("");
}
//# sourceMappingURL=largura-fixa.js.map